import React from 'react';
import {LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend} from 'recharts';
import {Portfolio} from "../../create-portfolio";
import {PortfolioYear} from "../../types/syndication";
import {useTheme} from "@mui/material";
import Typography from "@mui/material/Typography";
import {getAbbreviatedCurrencyValue} from "../../util";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

interface ChartsProps {
    portfolio: Portfolio;
    comparisonPortfolio: PortfolioYear[],
}

/*
Period (start-of-year to end-of-2022)	Average annual S&P 500 return
5 years (2018-2022)	7.51%
10 years (2013-2022)	10.41%
20 years (2003-2022)	7.64%
30 years (1993-2022)	7.52%

 */

const Charts: React.FC<ChartsProps> = ({portfolio, comparisonPortfolio}) => {
    const theme = useTheme();

    const passiveIncomeData: Record<string, any>[] = portfolio.years.map((year) => {
        return {
            name: year.year,
            'Conveyor Belt deals': year.passiveIncome,
        }
    });

    const totalEquityData: Record<string, any>[] = portfolio.years.map((year) => {
        return {
            name: year.year,
            'Conveyor Belt deals': year.totalEquity,
        }
    });

    comparisonPortfolio.forEach((year, i) => {
        totalEquityData[i]['Comparison Investment'] = year.totalEquity;
        passiveIncomeData[i]['Comparison Investment'] = year.passiveIncome;
    });

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    const tooltipFormatter = (value: number) => {
        return formatter.format(value || 0);
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h4">Total Equity</Typography>
                    <LineChart width={400} height={400} data={totalEquityData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="name" 
                            stroke={theme.palette.grey[800]}
                        />
                        <YAxis 
                            tickFormatter={getAbbreviatedCurrencyValue}
                            stroke={theme.palette.grey[800]}
                        />
                        <Tooltip 
                            formatter={tooltipFormatter}
                            contentStyle={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '4px',
                                padding: '8px',
                                boxShadow: theme.shadows[2]
                            }}
                        />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="Conveyor Belt deals"
                            stroke={theme.palette.primary.main}
                            isAnimationActive={false}
                            className='conveyor-belt-deals-line'
                        />
                        <Line
                            type="monotone"
                            dataKey="Comparison Investment"
                            stroke={theme.palette.grey[600]}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h4">Passive Income</Typography>
                    <LineChart width={400} height={400} data={passiveIncomeData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="name" 
                            stroke={theme.palette.grey[800]}
                        />
                        <YAxis 
                            tickFormatter={getAbbreviatedCurrencyValue}
                            stroke={theme.palette.grey[800]}
                        />
                        <Tooltip 
                            formatter={tooltipFormatter}
                            contentStyle={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '4px',
                                padding: '8px',
                                boxShadow: theme.shadows[2]
                            }}
                        />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="Conveyor Belt deals"
                            stroke={theme.palette.secondary.main}
                            isAnimationActive={false}
                            className='conveyor-belt-deals-line'
                        />
                        <Line
                            type="monotone"
                            dataKey="Comparison Investment"
                            stroke={theme.palette.grey[600]}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </Box>
            </Grid>
        </Grid>
    );
};

export default Charts;